import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, IconButton, Tooltip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import FindInPageIcon from "@mui/icons-material/FindInPage";
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HubIcon from '@mui/icons-material/Hub';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

import Overview from './Overview';
import Facilities from './Facilities';
import LcmRules from './LcmRules';
import StorageStats from './StorageStats';
import StorageBackends from './StorageBackends';
import Configuration from './Configuration';
import AuditLog from './AuditLog';
import Users from './Users';
import DicomTools from './DicomTools';
import DicomAeTitles from './DicomAeTitles';
import BuiltInMonitoring from './BuiltInMonitoring';
import FhirSmartIntegration from './FhirSmartIntegration';
import ProfileEditor from './ProfileEditor';

const DRAWER_WIDTH = 220;
const ADMIN_RBAC_MONITORING_ONLY_MARKER = 'KAIROS_ADMIN_UI_MONITORING_ONLY';

const hasAdminRole = (user) => (user?.roles || []).some(role => ['admin', 'superadmin'].includes(String(role).toLowerCase()));

const NAV = [
  { key: 'overview',    label: 'Overview',       icon: <DashboardIcon />,    component: Overview },
  { key: 'users',       label: 'Users',           icon: <PeopleIcon />,       component: Users },
  { key: 'profile-editor', label: 'Profile Editor', icon: <ManageAccountsIcon />, component: ProfileEditor },
  { key: 'facilities',  label: 'Facilities',      icon: <BusinessIcon />,     component: Facilities },
  { key: 'lcm',         label: 'LCM Rules',       icon: <DeleteSweepIcon />,   component: LcmRules },
  { key: 'storage',     label: 'Storage',         icon: <StorageIcon />,      component: StorageStats },
  { key: 'storage-backends', label: 'Storage Backends', icon: <StorageIcon />, component: StorageBackends },
  { key: 'monitoring',  label: 'Built-in Monitoring', icon: <MonitorHeartIcon />, component: BuiltInMonitoring },
  { key: 'fhir-smart',  label: 'FHIR / SMART',   icon: <HubIcon />,          component: FhirSmartIntegration },
  { key: 'config',      label: 'Configuration',   icon: <SettingsIcon />,     component: Configuration },
  { key: 'audit',       label: 'Audit Log',       icon: <FindInPageIcon />, component: AuditLog },
  { key: 'dicom-ae-titles', label: 'DICOM AE Titles', icon: <SettingsInputAntennaIcon />, component: DicomAeTitles },
  { key: 'dicom-tools', label: 'DICOM Tools',     icon: <FindInPageIcon />, component: DicomTools },
];

const AdminPage = () => {
  const [active, setActive] = useState('overview');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = hasAdminRole(user);
  const navItems = isAdmin ? NAV : NAV.filter(item => item.key === 'monitoring');

  useEffect(() => {
    if (!isAdmin && active !== 'monitoring') {
      setActive('monitoring');
    }
  }, [active, isAdmin]);

  const activeItem = navItems.find(n => n.key === active) || navItems[0] || NAV[0];
  const ActiveComponent = activeItem.component || Overview;

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#0a0a0a' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            bgcolor: '#0d0d0d',
            borderRight: '1px solid #1e1e1e',
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to viewer">
            <IconButton size="small" onClick={() => navigate('/')} sx={{ color: '#555', '&:hover': { color: 'primary.main' } }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography sx={{ color: 'primary.main', fontWeight: 700, fontSize: 13, letterSpacing: 1.5 }} data-admin-rbac={ADMIN_RBAC_MONITORING_ONLY_MARKER}>
              {isAdmin ? 'KAIROS ADMIN' : 'KAIROS MONITORING'}
            </Typography>
            <Typography sx={{ color: '#444', fontSize: 10, letterSpacing: 0.5 }}>
              {isAdmin ? 'MANAGEMENT CONSOLE' : 'READ-ONLY SYSTEM STATUS'}
            </Typography>
          </Box>
        </Box>

        {/* Nav */}
        <List sx={{ pt: 1 }}>
          {navItems.map(item => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                selected={activeItem.key === item.key}
                onClick={() => setActive(item.key)}
                sx={{
                  mx: 1, mb: 0.3, borderRadius: 1,
                  '&.Mui-selected': {
                    bgcolor: '#0d2a2f',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                    '& .MuiListItemText-primary': { color: 'primary.main' },
                  },
                  '&:hover': { bgcolor: '#141414' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: '#555', fontSize: 20 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13, color: '#aaa', fontFamily: 'monospace' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ flex: 1 }} />
        <Divider sx={{ borderColor: '#1e1e1e' }} />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ color: '#333', fontSize: 10, fontFamily: 'monospace' }}>
            v0.3.1 · Kairos
          </Typography>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: '#0a0a0a' }}>
        <ActiveComponent />
      </Box>
    </Box>
  );
};

export default AdminPage;
